import { Button, Result } from 'antd';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { workspaceSearch } from './layout/workspaces';
import { canAny, useAuthority } from '../lib/permissions';
import { ROUTES } from './routes';

export type RequirePermissionProps = {
  /** The user needs at least one of these codes. */
  anyOf: readonly string[];
  children: ReactNode;
};

/**
 * Screen-level permission guard.
 *
 * **Renders a refusal, never a redirect.** A redirect to the dashboard would
 * be a loop for a user whose default screen is the one being refused, and it
 * hides the reason. The nav item is hidden anyway (`nav-items.tsx`), so
 * arriving here means a typed URL, a stale bookmark, or a permission that was
 * revoked mid-session — all cases where an explanation beats a bounce.
 *
 * The backend is the real guard: the same request answers `403 FORBIDDEN`
 * whatever this renders (CLAUDE.md rule 3).
 *
 * A super-admin-role holder passes because `GET /auth/me` returns the live
 * permission set; the bypass itself exists only on the server and is never a
 * client-side flag (Story D2).
 */
export function RequirePermission({ anyOf, children }: RequirePermissionProps) {
  const authority = useAuthority();
  const { search } = useLocation();

  if (!canAny(authority, anyOf)) {
    return (
      <Result
        status="403"
        title="You do not have access to this screen"
        subTitle="Ask an administrator if you need it. Your other screens are unaffected."
        extra={
          /*
           * The workspace query parameter (ADR-0012) survives the bounce —
           * otherwise the escape hatch from a refused screen also silently
           * changes workspace. Only that key: `record`, `mode`, `page` and the
           * filters belong to the module being left (`workspaces.ts`).
           */
          <Link to={{ pathname: ROUTES.dashboard, search: workspaceSearch(search) }}>
            <Button type="primary">Go to the dashboard</Button>
          </Link>
        }
      />
    );
  }

  return <>{children}</>;
}
